// PII upload guard — scans file text content before storage
// Text files: client-side scan. Binary files: server-side extraction + scan.
import { detectPII } from './pii-detection'
import { supabase } from './supabase'
import type { PIIDetectionResult } from '@/types/pii'

const TEXT_TYPES = ['text/plain', 'text/markdown', 'text/csv', 'text/html']
const TEXT_EXTENSIONS = ['txt', 'md', 'csv', 'html', 'log', 'eml']
const MAX_SCAN_SIZE = 50000

/**
 * Scan a file for PII before permanent upload.
 * Text files: read client-side + call PII agent.
 * Binary files: upload to temp, call server-side extraction + PII scan.
 */
export async function scanFileForPII(
  file: File,
  customerId?: string,
): Promise<PIIDetectionResult> {
  const ext = file.name.split('.').pop()?.toLowerCase() || ''
  const isText = TEXT_TYPES.includes(file.type) || TEXT_EXTENSIONS.includes(ext)

  if (isText) {
    return scanTextFile(file)
  }

  // Binary files — upload to temp, extract text server-side, scan PII
  return scanBinaryFile(file, customerId)
}

async function scanTextFile(file: File): Promise<PIIDetectionResult> {
  const text = await readFileText(file)
  if (!text.trim() || text.length < 10) {
    return { pii_found: [], total_found: 0, has_critical: false, redacted_text: text || '(empty file)' }
  }

  try {
    return await detectPII(text)
  } catch (err) {
    console.error('[PII] Text file scan failed:', err)
    const errorMsg = err instanceof Error ? err.message : 'Unknown error'
    throw new Error(`Text PII scan failed: ${errorMsg}`)
  }
}

async function scanBinaryFile(file: File, _customerId?: string): Promise<PIIDetectionResult> {
  const tempPath = `temp-pii-scan/${Date.now()}_${file.name}`

  try {
    // Upload to temp location
    const { error: uploadErr } = await supabase.storage
      .from('customer-documents')
      .upload(tempPath, file)

    if (uploadErr) {
      throw new Error(`Temp upload failed: ${uploadErr.message}`)
    }

    // Call server-side extraction + PII scan
    const { data: { session } } = await supabase.auth.getSession()
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL

    const resp = await fetch(`${supabaseUrl}/functions/v1/scan-document-pii`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${session?.access_token ?? ''}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ storagePath: tempPath, bucket: 'customer-documents' }),
    })

    // Clean up temp file
    supabase.storage.from('customer-documents').remove([tempPath]).catch(() => {})

    if (!resp.ok) {
      const errText = await resp.text()
      console.error('[PII] Server scan failed:', resp.status, errText)
      throw new Error(`Server PII scan failed (${resp.status}): ${errText.substring(0, 100)}`)
    }

    const result = await resp.json()

    if (!result.success) {
      throw new Error(result.error || 'PII scan returned unsuccessful result')
    }

    if (!result.extractedText || result.extractedText.trim().length < 10) {
      console.warn('[PII] No text extracted from binary file')
      return {
        pii_found: [],
        total_found: 0,
        has_critical: false,
        redacted_text: `[No text could be extracted from ${file.name}. File may be image-based or corrupted.]`,
      }
    }

    return {
      pii_found: result.pii?.pii_found || [],
      total_found: result.pii?.total_found || 0,
      has_critical: result.pii?.has_critical || false,
      redacted_text: result.pii?.redacted_text || result.extractedText,
    }
  } catch (err) {
    console.error('[PII] Binary file scan error:', err)
    // Clean up temp file on error
    supabase.storage.from('customer-documents').remove([tempPath]).catch(() => {})
    const errorMsg = err instanceof Error ? err.message : 'Unknown error during PII scan'
    throw new Error(errorMsg)
  }
}

function readFileText(file: File): Promise<string> {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = () => resolve((reader.result as string).slice(0, MAX_SCAN_SIZE))
    reader.onerror = () => resolve('')
    reader.readAsText(file)
  })
}
